import React, { useState } from 'react';
import { X, AlertTriangle, Zap, Target, ChevronRight } from 'lucide-react';
import MathText from '../../VueQuestion/MathText';

/**
 * Panneau « attendus non abordés » — couverture du programme par la
 * progression commune du niveau (arbitrages prof 2026-08-25) : seuls les
 * ATTENDUS comptent (jamais les savoir-faires), tout type d'activité
 * aborde, et les manquants sont regroupés par catégorie : deux sections
 * (objectifs / automatismes) puis les thèmes du programme.
 * ACCORDÉON à deux niveaux (retour prof 2026-08-25 : ~50 manquants en
 * liste plate, illisible) : sections ET thèmes dépliables, les compteurs
 * restent toujours visibles — on scanne les volumes, on ouvre ce qu'on
 * lit. Objectifs ouvert par défaut, thèmes repliés.
 */
const GroupeTheme = ({ theme, attendus, ouvert, onToggle, styles }) => (
    <div className="mb-1.5">
        <button
            onClick={onToggle}
            className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-all text-left ${styles.themeEntete} ${ouvert ? 'shadow-sm' : ''}`}
        >
            <span className={`flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center ${styles.themeChevronFond}`}>
                <ChevronRight
                    size={12}
                    className={`transition-transform duration-200 ${styles.themeChevron}`}
                    style={{ transform: ouvert ? 'rotate(90deg)' : 'none' }}
                />
            </span>
            <span className={`flex-1 text-[10px] font-extrabold tracking-widest uppercase ${styles.themeTexte}`}>
                {theme}
            </span>
            <span className={`text-[10px] font-black rounded-full px-2 py-0.5 ${styles.themeBadge}`}>
                {attendus.length}
            </span>
        </button>
        {ouvert ? (
            <div className="flex flex-col gap-1.5 mt-1 ml-3">
                {attendus.map((att) => (
                    <div
                        key={att.id}
                        className="flex items-start gap-2.5 p-2.5 rounded-xl border border-slate-200 bg-white shadow-sm"
                    >
                        <span className="flex-shrink-0 mt-0.5 px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500 font-mono text-[10px] font-bold">
                            {att.id}
                        </span>
                        <p className="text-[12.5px] text-slate-700 leading-snug">
                            <MathText>{att.description}</MathText>
                        </p>
                    </div>
                ))}
            </div>
        ) : null}
    </div>
);

// Classes ÉCRITES EN CLAIR par section (Tailwind purge les noms construits)
const STYLES_SECTIONS = {
    objectifs: {
        entete: 'bg-blue-50 border-2 border-blue-200 hover:bg-blue-100 hover:border-blue-300 text-blue-700',
        chevronFond: 'bg-blue-500',
        badge: 'bg-blue-500 text-white',
        themeEntete: 'bg-blue-50/50 border-blue-100 hover:bg-blue-50 hover:border-blue-200',
        themeChevronFond: 'bg-blue-100',
        themeChevron: 'text-blue-600',
        themeTexte: 'text-blue-700/80',
        themeBadge: 'bg-white border border-blue-200 text-blue-600',
    },
    automatismes: {
        entete: 'bg-emerald-50 border-2 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 text-emerald-700',
        chevronFond: 'bg-emerald-500',
        badge: 'bg-emerald-500 text-white',
        themeEntete: 'bg-emerald-50/50 border-emerald-100 hover:bg-emerald-50 hover:border-emerald-200',
        themeChevronFond: 'bg-emerald-100',
        themeChevron: 'text-emerald-600',
        themeTexte: 'text-emerald-700/80',
        themeBadge: 'bg-white border border-emerald-200 text-emerald-600',
    },
};

const Section = ({ icone, titre, groupes, nb, ouvert, onToggle, themesOuverts, onToggleTheme, cle }) => {
    const styles = STYLES_SECTIONS[cle];
    return (
    <div className="mb-2">
        <button
            onClick={onToggle}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all text-left shadow-sm ${styles.entete}`}
        >
            <span className={`flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-white ${styles.chevronFond}`}>
                <ChevronRight
                    size={14}
                    className="transition-transform duration-200"
                    style={{ transform: ouvert ? 'rotate(90deg)' : 'none' }}
                />
            </span>
            {icone}
            <p className="flex-1 text-[13px] font-extrabold">{titre}</p>
            <span className={`text-[11px] font-black rounded-full px-2.5 py-0.5 ${styles.badge}`}>{nb}</span>
        </button>
        {ouvert ? (
            nb === 0 ? (
                <p className="text-xs text-slate-400 px-3 py-1.5">Tout est abordé ✔</p>
            ) : (
                <div className="mt-1.5 ml-2">
                    {groupes.map((g) => (
                        <GroupeTheme
                            key={g.theme}
                            theme={g.theme}
                            attendus={g.attendus}
                            ouvert={!!themesOuverts[`${cle}::${g.theme}`]}
                            onToggle={() => onToggleTheme(`${cle}::${g.theme}`)}
                            styles={styles}
                        />
                    ))}
                </div>
            )
        ) : null}
    </div>
    );
};

const PanneauNonAbordes = ({ isOpen, onClose, couverture }) => {
    const [sectionsOuvertes, setSectionsOuvertes] = useState({ objectifs: true, automatismes: false });
    const [themesOuverts, setThemesOuverts] = useState({});

    if (!isOpen || !couverture) return null;
    const { objectifs, automatismes, nbObjectifs, nbAutomatismes, avecAutomatismes } = couverture;

    const toggleSection = (cle) =>
        setSectionsOuvertes((prev) => ({ ...prev, [cle]: !prev[cle] }));
    const toggleTheme = (cle) =>
        setThemesOuverts((prev) => ({ ...prev, [cle]: !prev[cle] }));

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-2 sm:p-4">
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="relative bg-gradient-to-br from-amber-500 to-orange-500 px-5 py-4 text-white">
                    <button
                        onClick={onClose}
                        aria-label="Fermer"
                        className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                    >
                        <X size={15} />
                    </button>
                    <div className="flex items-center gap-2">
                        <AlertTriangle size={18} />
                        <h3 className="text-lg font-extrabold">Attendus non abordés</h3>
                    </div>
                    <p className="text-xs font-semibold opacity-90 mt-0.5">
                        {nbObjectifs} objectif{nbObjectifs > 1 ? 's' : ''}
                        {avecAutomatismes ? ` · ${nbAutomatismes} automatisme${nbAutomatismes > 1 ? 's' : ''}` : ''} absents de la progression commune
                    </p>
                </div>

                {/* Contenu */}
                <div className="p-3 max-h-[62vh] overflow-y-auto">
                    <Section
                        icone={<Target size={15} />}
                        titre="Objectifs d'apprentissage"
                        groupes={objectifs}
                        nb={nbObjectifs}
                        ouvert={!!sectionsOuvertes.objectifs}
                        onToggle={() => toggleSection('objectifs')}
                        themesOuverts={themesOuverts}
                        onToggleTheme={toggleTheme}
                        cle="objectifs"
                    />
                    {avecAutomatismes ? (
                        <Section
                            icone={<Zap size={15} />}
                            titre="Automatismes"
                            groupes={automatismes}
                            nb={nbAutomatismes}
                            ouvert={!!sectionsOuvertes.automatismes}
                            onToggle={() => toggleSection('automatismes')}
                            themesOuverts={themesOuverts}
                            onToggleTheme={toggleTheme}
                            cle="automatismes"
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default PanneauNonAbordes;
